/**
 * Dark Theme Configuration
 * 
 * Ant Design theme configuration for dark mode.
 * Uses darkAlgorithm with custom brand colors for optimal contrast.
 * Updated: 2025-12-10 - Vibrant blue text matching indicator arrows for consistency
 */

import type { ThemeConfig } from 'antd';
import { theme } from 'antd';
import { sharedTokens } from './shared';

export const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  
  token: {
    ...sharedTokens,
    
    // Brand Colors (brightened for dark mode visibility)
    colorPrimary: '#4096ff',
    colorSuccess: '#73d13d',
    colorWarning: '#ffc53d',
    colorError: '#ff7875',
    colorInfo: '#4096ff',
    colorLink: '#4096ff',
    
    // Text Colors (optimized for #0d1117 background) - Vibrant blue matching indicator
    colorText: '#4096ff',
    colorTextSecondary: '#69b1ff',
    colorTextTertiary: 'rgba(255, 255, 255, 0.78)',
    colorTextQuaternary: 'rgba(255, 255, 255, 0.60)',
    
    // Background Colors (GitHub Dark inspired)
    colorBgBase: '#0d1117',        // Main background (softer than pure black)
    colorBgContainer: '#161b22',   // Cards and containers
    colorBgElevated: '#1c2128',    // Elevated elements (modals, dropdowns)
    colorBgLayout: '#0d1117',      // Layout background
    colorBgSpotlight: 'rgba(255, 255, 255, 0.95)',
    colorBgMask: 'rgba(1, 4, 9, 0.65)',
    
    // Border Colors (visible but subtle)
    colorBorder: '#30363d',
    colorBorderSecondary: '#21262d',
    
    // Fill Colors (for interactive elements)
    colorFill: 'rgba(110, 118, 129, 0.4)',
    colorFillSecondary: 'rgba(110, 118, 129, 0.26)',
    colorFillTertiary: 'rgba(110, 118, 129, 0.15)',
    colorFillQuaternary: 'rgba(110, 118, 129, 0.08)',
  },
  
  components: {
    Layout: {
      headerBg: '#0d1117',
      headerColor: '#4096ff',
      headerHeight: 64,
      headerPadding: '0 24px',
      footerBg: '#0d1117',
      footerPadding: '16px 0',
      siderBg: '#0d1117',
      bodyBg: '#0d1117',
      triggerBg: '#21262d',
      triggerColor: 'rgba(255, 255, 255, 0.90)',
    },
    
    Menu: {
      darkItemBg: '#0d1117',
      darkItemColor: '#69b1ff',
      darkItemHoverBg: 'rgba(110, 118, 129, 0.15)',
      darkItemSelectedBg: '#1c2c44',
      darkItemSelectedColor: '#4096ff',
      subMenuItemBg: '#0d1117',
    },
    
    Card: {
      colorBgContainer: '#161b22',
      colorBorderSecondary: '#30363d',
      boxShadowTertiary: '0 1px 2px 0 rgba(0, 0, 0, 0.45), 0 1px 6px -1px rgba(0, 0, 0, 0.45), 0 2px 4px 0 rgba(0, 0, 0, 0.45)',
    },
    
    Table: {
      colorBgContainer: '#161b22',
      colorBorderSecondary: '#30363d',
      headerBg: '#0d1117',
      headerColor: '#4096ff',
      rowHoverBg: 'rgba(110, 118, 129, 0.15)',
      headerSplitColor: '#30363d',
    },
    
    Button: {
      primaryColor: '#ffffff',
      primaryShadow: '0 2px 0 rgba(64, 150, 255, 0.15)',
      defaultBorderColor: '#30363d',
      defaultColor: '#4096ff',
      defaultBg: '#21262d',
      defaultHoverBg: '#30363d',
      defaultHoverColor: '#69b1ff',
      defaultHoverBorderColor: '#4096ff',
    },
    
    Input: {
      colorBgContainer: '#0d1117',
      colorBorder: '#30363d',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.60)',
      hoverBorderColor: '#4096ff',
      activeBorderColor: '#4096ff',
      activeShadow: '0 0 0 2px rgba(64, 150, 255, 0.2)',
    },
    
    Select: {
      colorBgContainer: '#0d1117',
      colorBorder: '#30363d',
      colorTextPlaceholder: 'rgba(255, 255, 255, 0.60)',
      optionSelectedBg: '#1c2c44',
      optionActiveBg: 'rgba(110, 118, 129, 0.15)',
    },
    
    Modal: {
      contentBg: '#161b22',
      headerBg: '#161b22',
      titleColor: '#4096ff',
    },
    
    Drawer: {
      colorBgElevated: '#161b22',
    },
    
    Notification: {
      colorBgElevated: '#1c2128',
      colorBgContainer: '#1c2128',
      colorText: '#e5e8eb',
      colorTextSecondary: '#8b949e',
      colorBorder: '#30363d',
      boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.48), 0 6px 16px 0 rgba(0, 0, 0, 0.32), 0 9px 28px 8px rgba(0, 0, 0, 0.20)',
    },
    
    Message: {
      contentBg: '#1c2128',
      colorBgContainer: '#1c2128',
      colorText: '#e5e8eb',
      colorTextSecondary: '#8b949e',
      colorBorder: '#30363d',
      boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.48), 0 6px 16px 0 rgba(0, 0, 0, 0.32), 0 9px 28px 8px rgba(0, 0, 0, 0.20)',
    },
    
    Dropdown: {
      colorBgElevated: '#1c2128',
      controlItemBgHover: 'rgba(110, 118, 129, 0.15)',
      controlItemBgActive: '#1c2c44',
    },
    
    Tooltip: {
      colorBgSpotlight: '#1c2128',
    },
    
    Tag: {
      defaultBg: 'rgba(110, 118, 129, 0.15)',
      defaultColor: '#4096ff',
    },
  },
};
